import { forkJoin, Subscription, throwError as observableThrowError, Observable } from 'rxjs';

import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Yii } from "libraries/interfaces/Yii";
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ResourceError, ResourceStatus } from 'libraries/service/resource/ResourceError';
import { ViewsService } from "libraries/view/views.service";
import { IPopupMenuConfig } from 'libraries/component/popup-menu/popup-menu.component';
import { AppRootService } from 'app/shared/services/app-root/app-root-service';
import { ISummary } from 'libraries/component/search-summary/ISummary';
import { BookingSummaryService } from './booking-summary.service';
import { ProcessingService } from 'libraries/service/processing/processing.service';
import { IApiBookingSearchResponse } from "libraries/service/resources/booking/IApiBookingSearchResponse";
import { SashaPageComponent } from "../sasha-page.component";
import { BookingSearchParameters } from "app_sasha/shared/models/BookingSearchParameters";
import { ISearchStatus } from "app_sasha/shared/interfaces/ISearchStatus";
import { BookingResourceService } from "libraries/service/resources/booking/bookingResource.service";
import { SashaBreadcrumbService } from "app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service";
import { FIELD_LABELS, FIELD_PLURAL_LABELS } from "app_sasha/shared/config/booking-wording";
import { ILabels } from 'libraries/interfaces/ILabels';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { ICompleteNumberLabels, LabelService } from 'libraries/service/label/label.service';
import { DatesService } from "app_sasha/shared/services/dates/dates.service";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import { IBookingSearchParametersURL } from "libraries/interfaces/IBookingSearchParameters";
import { EnumsResourceService } from "libraries/service/booking/enums/enums-resource.service";

const POSSIBLE_PAGINATION_LENGTH: number[] = [BookingSearchParameters.DEFAULT_PAGINATION_NUMBER, 100, 500, 1000];

@Component({
    selector: 'search-result',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>

            <ng-container *ngIf="showForm; then formTemplate; else summaryTemplate"></ng-container>

            <ng-template #formTemplate>
                <booking-search-form
                        [searchParameters]="searchParams"
                        [focusOnInit]="true"
                        (onBookingSearchSubmit)="onSubmitForm($event)"
                        (reset)="onResetForm()">
                </booking-search-form>
            </ng-template>
            <ng-template #summaryTemplate>
                <search-summary
                        [fields]="summary"
                        (onRemove)="onSummaryChange($event)"
                        (onModifySearch)="redirectToForm()">
                </search-summary>

            </ng-template>

            <booking-results-container
                    [title]="labels.results"
                    [bookings]="(response && response.booking ? response.booking : [])"
                    [recordset]="(response && response.recordset ? response.recordset : {})"
                    [searchStatus]="searchStatus"
                    [searchParams]="searchParams">

                <ng-container *ngIf="response" class="booking-results-footer">
                    <pagination [listLength]="response.recordset.nbrecord"
                                [recordset]="response.recordset"
                                [pageSizeOptions]="possibleSizes"
                                (onPageChange)="onPageChange($event)"></pagination>
                </ng-container>
            </booking-results-container>
        </div>
    `,
    styleUrls: ['./booking-search-result.component.scss'],
})
export class BookingSearchResultComponent extends SashaPageComponent implements OnInit, OnDestroy {


    public searchParams: BookingSearchParameters;
    public isLoading: boolean;
    public error: string;
    public response: IApiBookingSearchResponse;
    public possibleSizes: number[] = null;
    public showForm: boolean = false;
    public processingMenuConfig: IPopupMenuConfig;
    public summary: ISummary[];
    public searchStatus: ISearchStatus;
    public queryParams: IBookingSearchParametersURL;


    public labels: ILabels = {
        error: Yii.t('wui-sasha-js', 'Erreur :'),
        loading: Yii.t('wui-sasha-js', 'Recherche des dossiers en cours'),
        results: Yii.t('wui-sasha-js', 'Résultats de la recherche'),
    };


    private readonly titleLabels: ICompleteNumberLabels = {
        none: Yii.t('wui-sasha-js', 'Aucun dossier'),
        one: Yii.t('wui-sasha-js', '1 dossier'),
        default: Yii.t('wui-sasha-js', '%placeholder% dossiers'),
    };

    private routeSubscription: Subscription;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly bookingResourceService: BookingResourceService,
        private readonly breadcrumbService: SashaBreadcrumbService,
        private readonly router: Router,
        private readonly summaryService: BookingSummaryService,
        private readonly processingService: ProcessingService,
        protected views: ViewsService,
        appRootService: AppRootService,
        private readonly titleService: TitleService,
        private readonly labelService: LabelService,
        private readonly datesService: DatesService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly enumsResourceService: EnumsResourceService
    ) {
        super(views, appRootService);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.searchStatus = {
            isLoading: false,
            isError: false
        };

        // Performance purpose : launch wordings for quality check here, this way it show quickly if needed in results
        this.enumsResourceService.listQualityCheck()
            .subscribe({
                next: () => {
                    console.info('quality check translations loaded');
                }
            });

        this.routeSubscription = this.route
            .queryParams
            .pipe(
                tap((queryParams: IBookingSearchParametersURL) => {
                    this.showForm = Boolean(queryParams.form);
                    this.queryParams = queryParams;
                    this.titleService.setTitle(Yii.t("wui-sasha-js", "Recherche de dossiers..."));
                    this.summary = [];
                }),
                map((queryParams: IBookingSearchParametersURL) => BookingSearchParameters.fromRouteParams(queryParams, this.datesService)),
                tap((params: BookingSearchParameters) => {
                    this.searchStatus = this.getSearchStatus({isLoading: true});
                    this.response = undefined;
                    this.searchParams = params;
                    params.afidiumServices = BookingSearchParameters.fromRouteParamsAfidiumService(this.queryParams, 'afs');
                    this.breadcrumbService.setSearchResult(params);
                }),
                switchMap((searchParameters: BookingSearchParameters) =>
                    forkJoin(
                        this.bookingResourceService.search(searchParameters).pipe(
                            map((response: IApiBookingSearchResponse) => {
                                this.response = response;
                                this.titleService.setTitle(
                                    this.labelService.completeNumber(
                                        this.response.recordset.total,
                                        this.titleLabels
                                    ),
                                    Yii.t("wui-sasha-js", "Recherche de dossiers")
                                );

                                this.possibleSizes = POSSIBLE_PAGINATION_LENGTH;
                                this.searchStatus = this.getSearchStatus({isLoading: false});
                            }),
                            catchError((error: ResourceError) => {
                                if (error.status === ResourceStatus.NotAuthorized) {
                                    this.router.navigate(['/login']).catch((reason: boolean) => {
                                        this.errorTracingService.captureException(reason);
                                    });
                                }

                                return observableThrowError(error);
                            })),

                        this.summaryService.createSummary(searchParameters, FIELD_LABELS, FIELD_PLURAL_LABELS).pipe(
                            tap((inputSummaries: ISummary[]) => {
                                this.summary.push(...inputSummaries);
                            })
                        ),

                        this.summaryService.createAfidiumServiceSummaries(this.queryParams, 'afs').pipe(
                            tap((serviceSummaries: ISummary[]) => {
                                this.summary.push(...serviceSummaries);
                            })
                        )
                    )
                )
            )
            .subscribe({
                error: (error: Error): void => {
                    this.errorTracingService.captureException(new RuntimeError("Error while loading the page", error));
                    this.searchStatus = this.getSearchStatus({
                        isError: true,
                        isLoading: false,
                        errorMessage: error.message
                    });
                },
                complete: (): void => {
                    this.searchStatus = this.getSearchStatus({isLoading: false});
                }
            });

        // initiate treatement menu configuration
        this.processingMenuConfig = this.processingService.getMenuConfig();
    }

    public ngOnDestroy(): void {
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    public onPageChange(event: { first: number, rows: number }): void {
        const newSearchParameters: BookingSearchParameters = this.searchParams.clone();
        newSearchParameters.nbrecord = event.rows;
        newSearchParameters.first = event.first + 1;

        this.router.navigate([], {queryParams: newSearchParameters.convertToUrlParameters()})
            .catch((reason: boolean) => {
                this.errorTracingService.captureException(reason);
            });
    }

    public onSummaryChange(value: ISummary[]): void {
        const removed: string[] = value.map((summary: ISummary) => summary.field);
        const newSearchParameters: BookingSearchParameters = this.searchParams.clone().clearValues(removed);

        // Reinitialize the pagination when modifying the search parameters
        newSearchParameters.first = 1;

        const queryParams: IBookingSearchParametersURL = newSearchParameters.convertToUrlParameters();

        this.router.navigate([], {queryParams})
            .catch((reason: boolean) => {
                this.errorTracingService.captureException(reason);
            });
    }

    public redirectToForm(): void {
        const queryParams: IBookingSearchParametersURL = {
            ...this.searchParams.convertToUrlParameters(),
            form: true
        };
        this.router.navigate(
            ['.'],
            {
                relativeTo: this.route,
                queryParams,
            }
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    public onSubmitForm(searchParameters: BookingSearchParameters): void {

        const queryParams: IBookingSearchParametersURL = searchParameters.convertToUrlParameters();

        // first refresh to the route with the open form
        this.router.navigate(
            ['.'],
            {
                relativeTo: this.route,
                queryParams: {...queryParams, form: true},
            }
        ).then(// then change route to hide the form
            async (): Promise<boolean> => this.router.navigate(
                ['.'],
                {
                    relativeTo: this.route,
                    queryParams,
                }
            )
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    public onResetForm(): void {
        const searchParameters: BookingSearchParameters = new BookingSearchParameters();
        const queryParams: IBookingSearchParametersURL = searchParameters.convertToUrlParameters();

        this.router.navigate(
            ['/sasha/booking/search'],
            {
                queryParams,
            }
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    private getSearchStatus(newStatus: ISearchStatus): ISearchStatus {
        return {
            ...this.searchStatus,
            ...newStatus
        };
    }
}
